/**
 * Disclosure-style "More…" menu used on the Account page. Opens
 * downward, closes on outside click, ESC, or selecting an item.
 *
 * The destructive action is below the divider to make it harder to mis-click.
 */

import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import Icon, { type IconName } from "../../components/Icon";

interface MoreMenuItemProps {
  label: string;
  onSelect: () => void;
  destructive?: boolean;
}

export function MoreMenuItem({
  label,
  onSelect,
  destructive,
}: MoreMenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={
        "more-menu__item" +
        (destructive ? " more-menu__item--danger" : "")
      }
      onClick={onSelect}
    >
      {label}
    </button>
  );
}

export function MoreMenuDivider() {
  return <div className="more-menu__divider" role="separator" />;
}

/**
 * Wraps the trigger + dropdown. The dropdown content is rendered
 * imperatively via a render-prop so the parent can intercept clicks
 * (closing the menu before opening a modal, for example).
 */
export default function MoreMenu({
  render,
  triggerIcon,
  triggerAriaLabel,
  triggerClassName,
}: {
  render: (helpers: { close: () => void }) => ReactNode;
  triggerIcon?: IconName;
  triggerAriaLabel?: string;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside-click and on ESC.
  useEffect(() => {
    if (!open) return;
    function handlePointer(event: PointerEvent) {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      if (event.target instanceof Node && wrapper.contains(event.target)) {
        return;
      }
      setOpen(false);
    }
    function handleKey(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("pointerdown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  function handleTriggerKey(event: ReactKeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setOpen(true);
    }
  }

  return (
    <div className="more-menu" ref={wrapperRef}>
      <button
        type="button"
        ref={buttonRef}
        className={
          "button button--ghost more-menu__trigger" +
          (triggerIcon ? " more-menu__trigger--icon" : "") +
          (triggerClassName ? ` ${triggerClassName}` : "")
        }
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={triggerIcon ? triggerAriaLabel : undefined}
        title={triggerIcon ? triggerAriaLabel : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleTriggerKey}
      >
        {triggerIcon ? (
          <Icon name={triggerIcon} />
        ) : (
          <>
            <span>More</span>
            <Icon name="chevron" className="more-menu__chevron" />
          </>
        )}
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="more-menu__panel"
          // Close after any click that lands on a menuitem; the menuitem's
          // own onSelect runs first because click bubbles.
          onClick={(event) => {
            const target = event.target as HTMLElement;
            if (target.closest('[role="menuitem"]')) setOpen(false);
          }}
        >
          {render({ close: () => setOpen(false) })}
        </div>
      ) : null}
    </div>
  );
}
